import { Children, ReactNode, isValidElement } from 'react';
import {
  Stack,
  FormControlLabel,
  Card,
  CardActionArea,
  Collapse,
  Button,
  BoxProps,
  useTheme,
} from '@mui/material';
import { MembershipPlan } from '../../membership.constants';

type ContentProps = {
  children: ReactNode;
};

type PlanCardProps = ContentProps & {
  name: string;
  value: MembershipPlan | 'donate' | 'teams';
  onChange: (value: MembershipPlan | 'donate' | 'teams') => void;
  checked: boolean;
  displayActive?: boolean;
  buttonTitle: string;
  bgcolor?: BoxProps['bgcolor'];
};

export const PlanCard = (props: PlanCardProps): JSX.Element => {
  const {
    name,
    value,
    onChange,
    checked,
    children,
    buttonTitle,
    displayActive = false,
    bgcolor,
  } = props;
  const isOpen = checked || displayActive;
  const theme = useTheme();

  const Content = Children.toArray(children).find(
    (child) => isValidElement(child) && child.type === PlanCard.Content,
  );
  const CollapseContent = Children.toArray(children).find(
    (child) => isValidElement(child) && child.type === PlanCard.CollapseContent,
  );

  const borderColor = theme.palette.brand[500];

  return (
    <FormControlLabel
      id={`${name}-${value}`}
      aria-label={name}
      value={value}
      control={
        <Card
          elevation={0}
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: '24px',
            ...(isOpen && {
              border: `3px solid ${borderColor}`,
            }),
            bgcolor,
          }}
        >
          <CardActionArea
            disabled={isOpen}
            sx={
              isOpen
                ? {
                    '&:hover .MuiCardActionArea-focusHighlight': {
                      opacity: 0,
                    },
                  }
                : undefined
            }
            role="radio"
            aria-checked={checked}
            aria-labelledby={`${name}-${value}`}
          >
            <Stack spacing={2} sx={{ p: 3, pb: isOpen ? 1 : 3 }}>
              {Content}

              {!isOpen && (
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  color="inherit"
                  component="span"
                >
                  {buttonTitle}
                </Button>
              )}
            </Stack>
          </CardActionArea>

          {CollapseContent && (
            <Collapse in={isOpen}>
              <Stack spacing={2} sx={{ p: 3, pt: 0 }}>
                {CollapseContent}
              </Stack>
            </Collapse>
          )}
        </Card>
      }
      label={null}
      disabled={isOpen}
      onClick={() => {
        if (isOpen) return;
        onChange(value);
      }}
    />
  );
};

PlanCard.Content = function Content({ children }: ContentProps) {
  return children;
};

PlanCard.CollapseContent = function CollapseContent({
  children,
}: ContentProps) {
  return children;
};
